import React from "react";
import { useNavigate } from "react-router-dom";
import reverse from "../../../assets/reverse.svg";
import tick from "../../../assets/tick.svg";
import cancel from "../../../assets/cancel.svg";

/**
 * TopActions component - Handles top action buttons for camera interface
 *
 * Features:
 * - Shows camera options and flip buttons when in camera mode
 * - Displays accept/cancel buttons when image is selected
 * - Shows view uploaded images button when user has uploaded images
 * - Hides buttons when camera has errors
 * - Conditional rendering based on app state
 * - Responsive button layout with proper styling
 *
 * Props:
 * @param {string|null} selectedImage - Currently selected/captured image data URL
 * @param {string|null} cameraError - Current camera error state
 * @param {Function} setShowOptions - Function to toggle options panel visibility
 * @param {Function} flipCamera - Function to switch between front/rear camera
 * @param {Function} setSelectedImage - Function to clear selected image
 * @param {Function} setOriginalImage - Function to clear original image
 * @param {Function} setActiveFilter - Function to reset filter to "Normal"
 * @param {number} uploadedCount - Number of images uploaded by user
 * @param {boolean} canUpload - Whether user can upload more images
 */
const TopActions = ({
  hasSelection, // Currently selected/captured image
  cameraError, // Current camera error state
  setShowOptions, // Function to toggle options panel
  flipCamera, // Function to flip camera direction
  onUploadImage, // Function to handle image upload
  uploadedCount, // Number of uploaded images
  onRemoveActive,
  canUpload,
  event_id,
}) => {
  const navigate = useNavigate();

  const ViewImageBox =
    uploadedCount > 0 ? (
      <button
        className="view-images-top-btn"
        onClick={() => navigate("/user-uploaded-images?eventId=" + event_id)}
      >
        View Album ({uploadedCount})
      </button>
    ) : null;

  return (
    <>
      {/* 
        Camera mode actions
        - Shows when no image is selected and no camera error
        - Contains options menu and camera flip buttons
      */}
      {!hasSelection && !cameraError ? (
        <div className="top-actions">
          {/* 
            Options menu button
            - Opens camera settings dropdown (flash, timer, ratio)
            - Uses three dots icon for menu indication
          */}
          <button
            className="icon-btn"
            onClick={() => setShowOptions((prev) => !prev)}
          >
            ⋯
          </button>
          {ViewImageBox}
          {/* 
            Camera flip button
            - Switches between front and rear camera
            - Uses reverse/flip icon for visual indication
          */}
          <button className="icon-btn" onClick={flipCamera}>
            <img src={reverse} alt="Reverse" className="option-icon" />
          </button>

          {/* 
            View uploaded images button
            - Shows only when user has uploaded images
            - Navigates to uploaded images gallery
          */}
        </div>
      ) : hasSelection ? (
        /* 
          Image selected actions
          - Shows when an image is captured or selected from gallery
          - Contains accept and cancel buttons for image confirmation
          - Uses special styling class for right-aligned layout
        */
        <div className="top-actions accept-reject-actions">
          {ViewImageBox}
          {/* 
            Accept button
            - Confirms the current image selection
          */}
          <button className="icon-btn" onClick={onUploadImage}>
            <img src={tick} alt="Accept" className="option-icon" />
          </button>

          {/* 
            Cancel button
            - Rejects current image and returns to camera view
            - Clears selected image, original image, and resets filter
          */}
          <button className="icon-btn" onClick={onRemoveActive}>
            <img src={cancel} alt="Cancel" className="option-icon" />
          </button>
        </div>
      ) : canUpload && (
        <div className="top-actions accept-reject-actions">{ViewImageBox}</div>
      )}
    </>
  );
};

export default TopActions;
